import React, { useEffect, useState } from "react";
import { addressService, AddressResponse } from "../service/addressService";
import { useToast } from "@customHooks/useToast";
import { Loader2 } from "lucide-react";
import { Badge } from "@components/shadcn-ui/badge";

interface AddressDisplayProps {
  applicationId: string;
}

export const AddressDisplay: React.FC<AddressDisplayProps> = ({ applicationId }) => {
  const [addresses, setAddresses] = useState<AddressResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (applicationId) {
      fetchAddresses();
    } else {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const fetchAddresses = async () => {
    setIsLoading(true);
    try {
      const response = await addressService.getAddressesByApplicationId(applicationId);
      setAddresses(response.addresses || []);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      toast({
        variant: "destructive",
        description: "Failed to load addresses",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getAddressTypeColor = (type: string) => {
    switch (type) {
      case "PERMANENT":
        return "bg-green-100 text-green-800";
      case "CURRENT":
        return "bg-blue-100 text-blue-800";
      case "OFFICE":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getOwnershipBadge = (ownership: string) => {
    switch (ownership) {
      case "OWNED":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Owned</Badge>;
      case "RENTED":
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Rented</Badge>;
      case "LEASED":
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Leased</Badge>;
      case "COMPANY_PROVIDED":
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Company Provided</Badge>;
      case "FAMILY_OWNED":
        return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Family Owned</Badge>;
      default:
        return <Badge variant="outline">{ownership}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
        <span>Loading addresses...</span>
      </div>
    );
  }

  if (addresses.length === 0) {
    return (
      <div className="text-center p-4 border border-dashed rounded-md">
        <p className="text-gray-500">No addresses found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {addresses.map((address) => (
        <div key={address.id} className="border border-gray-200 rounded-md p-3">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={getAddressTypeColor(address.type)}>
              {address.type}
            </Badge>
            {getOwnershipBadge(address.ownership)}
          </div>
          <div className="text-sm">
            <p className="font-medium">{address.line1}</p>
            {address.line2 && <p>{address.line2}</p>}
            {address.landmark && <p>Landmark: {address.landmark}</p>}
            <p>
              {address.city}, {address.state}, {address.postalCode ? address.postalCode : ''}
            </p>
            <p>{address.country}</p>
            <div className="mt-2 text-gray-600 text-xs">
              <p>Years of stay: {address.yearsOfStay}</p>
              {address.monthlyRent && <p>Monthly rent: ₹{address.monthlyRent}</p>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
